/**
 * D-192: "Practice while you wait": the Quick match seat stayed in the queue, and the server says the table filled.
 * A card over any screen (the practice table included) with Join, which leaves the practice table and goes to the
 * Quick match one, and Not now. The table plays your turns until you're there.
 */
import { useEffect, useState, type ReactElement } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "../lib/session";
import { onNotify } from "../lib/ws";
import { leaveTable } from "../lib/table";
import { errorSentence } from "../lib/api";
import { Button } from "./Button";

export function QueueReadyCard(): ReactElement | null {
  const { status, currentRoom } = useSession();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [code, setCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (status !== "signed-in") return;
    return onNotify((m) => {
      const p = m.payload as { roomCode?: string; queueReady?: boolean } | null;
      if (m.kind === "info" && p?.queueReady && p.roomCode) { setCode(p.roomCode); setError(null); }
    });
  }, [status]);
  if (!code || pathname === `/table/${code}` || pathname === `/lobby/${code}`) return null;
  const join = async () => {
    setBusy(true); setError(null);
    try {
      const here = /^\/(?:table|lobby)\/([A-Z0-9]{8})/.exec(pathname)?.[1] ?? currentRoom;
      if (here && here !== code) await leaveTable(here).catch(() => undefined);
      setCode(null);
      navigate(`/table/${code}`);
    } catch (e) { setError(errorSentence(e)); }
    finally { setBusy(false); }
  };
  return (
    // D-211: at a table (the practice you play while waiting) it sits under the header, clear of its buttons.
    <div className={/^\/table\//.test(pathname) ? "tinv tinv--below-head" : "tinv"} role="alertdialog" aria-labelledby="qready-title" data-testid="queue-ready">
      <div className="tinv__card">
        <div className="tinv__head">
          <div className="tinv__text">
            <p className="body-b" id="qready-title">Your table is ready</p>
            <p className="small text-secondary">Your Quick match filled and the cards are dealt. Join now, or the table plays your turns.</p>
          </div>
        </div>
        {error ? <p className="small tinv__error" role="alert">{error}</p> : null}
        <div className="tinv__actions">
          <Button size={48} onClick={() => void join()} busy={busy}>Join</Button>
          <Button kind="quiet" size={48} onClick={() => setCode(null)}>Not now</Button>
        </div>
      </div>
    </div>
  );
}
